import { useReleaseNotes } from '../../api/release-notes';
import { Badge, EmptyState, ErrorState } from '../../components/ui';
import { formatReleaseDate, parseReleaseNotes, type ReleaseNoteBlock } from './release-notes-model';

/**
 * «О релизе» (#31, T.Z103) — the Settings renderer rule 0 of CLAUDE.md always
 * promised ("the changelog renders in the app's Settings page — a release
 * without a changelog entry does not deploy") and never had: until now the page
 * showed only the running version tag in the status footer.
 *
 * It sits under that footer on purpose: `status-version` and the newest block's
 * `tag` are the same value, so an operator reads the running build against the
 * release block that describes it. Blocks arrive newest-first from the api (the
 * file's own order); an api that cannot read its file answers `blocks: []`, and
 * that is «Нет данных», never an error page.
 */
export function ReleaseNotesSection() {
  const notes = useReleaseNotes();

  if (notes.isPending) {
    return (
      <div className="card" data-testid="release-notes-skeleton">
        <div className="release-notes-head">
          <SkeletonHead />
        </div>
        <div className="release-notes-body">
          <div className="skeleton skeleton--line skeleton--wide" />
          <div className="skeleton skeleton--line" />
        </div>
      </div>
    );
  }

  if (notes.isError) {
    return (
      <div className="card" data-testid="release-notes-error">
        <div className="release-notes-head">
          <Heading />
        </div>
        <div className="release-notes-body">
          <ErrorState
            message={notes.error instanceof Error ? notes.error.message : 'Не удалось получить список релизов.'}
            onRetry={() => void notes.refetch()}
          />
        </div>
      </div>
    );
  }

  const blocks = parseReleaseNotes(notes.data);

  return (
    <div className="card" data-testid="release-notes">
      <div className="release-notes-head">
        <Heading />
      </div>
      <div className="release-notes-body">
        {blocks.length === 0 ? (
          <EmptyState
            title="Нет данных"
            hint="changelog.txt недоступен — здесь появятся записи релизов, когда api ответит списком."
          />
        ) : (
          blocks.map((block) => <ReleaseNote key={`${block.tag} ${block.date}`} block={block} />)
        )}
      </div>
    </div>
  );
}

function Heading() {
  return (
    <>
      <h2 className="release-notes-title">О релизе</h2>
      <p className="release-notes-sub">Что изменилось в последних релизах — новые сверху.</p>
    </>
  );
}

function SkeletonHead() {
  return (
    <>
      <h2 className="release-notes-title">О релизе</h2>
      <div className="skeleton skeleton--line skeleton--wide" />
    </>
  );
}

function ReleaseNote({ block }: { block: ReleaseNoteBlock }) {
  return (
    <article className="release-note" data-testid="release-note-block">
      <p className="release-note__head">
        <span className="release-note__tag" data-testid="release-note-tag">
          {block.tag}
        </span>
        <span className="release-note__date" data-testid="release-note-date">
          {formatReleaseDate(block.date)}
        </span>
        {block.caption ? (
          <Badge>
            <span data-testid="release-note-caption">{block.caption}</span>
          </Badge>
        ) : null}
      </p>
      {block.items.length > 0 ? (
        <ul className="release-note__items" data-testid="release-note-items">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}
